import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import PageHeader from '../components/PageHeader.jsx'
import Pagination from '../components/Pagination.jsx'
import PartRequestEditModal from '../components/PartRequestEditModal.jsx'
import PartRequestModal from '../components/PartRequestModal.jsx'
import QuoteImportModal from '../components/QuoteImportModal.jsx'
import StatusBadge from '../components/StatusBadge.jsx'
import { useAuth } from '../components/AuthProvider.jsx'
import { useToast } from '../components/ToastProvider.jsx'
import {
  PART_REQUEST_STATUS_ACTIONS,
  PART_REQUEST_STATUS_FILTERS,
  PART_REQUEST_TYPE_FILTERS,
  requestItemName,
} from '../data/constants'
import * as partRequestApi from '../services/partRequestApi'
import { formatCompactDateTime, formatDateTime } from '../utils/format'
import { canEditPartRequest, formatExpectedTotal, formatUnitPrice } from '../utils/partRequestPurchase'
import { canManageRequests } from '../utils/permissions'

const PAGE_SIZES = [20, 50, 100]

function parsePage(value) {
  const n = Number.parseInt(value, 10)
  return Number.isInteger(n) && n > 0 ? n : 1
}

function parseLimit(value) {
  const n = Number.parseInt(value, 10)
  return PAGE_SIZES.includes(n) ? n : 20
}

function RequestStatusActions({ status, disabled, onSelect, size = 'small', layout = 'wrap' }) {
  const sizeClass = layout === 'table' ? 'table-action-btn' : size === 'touch' ? 'btn--touch' : 'btn--small'
  return (
    <div className={layout === 'table' ? 'request-status-actions' : 'user-actions'}>
      {PART_REQUEST_STATUS_ACTIONS.filter((item) => item.value !== status).map((item) => (
        <button
          key={item.value}
          type="button"
          className={`btn ${item.button} ${sizeClass}`}
          disabled={disabled}
          onClick={() => onSelect(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}

function PurchaseLink({ url }) {
  if (!url) return '-'
  return (
    <a className="request-link" href={url} target="_blank" rel="noopener noreferrer">
      구매처 열기
    </a>
  )
}

function RequestDetailModal({ open, request, canManage, canEdit, onClose, onEdit, onOpenQuotation, onStatusChange }) {
  const [managerMemo, setManagerMemo] = useState(request?.managerMemo || '')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !request) return
    setManagerMemo(request.managerMemo || '')
    setError('')
  }, [open, request])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || !request) return null

  const isNew = request.requestType === 'NEW_ITEM'
  const currentStock =
    request.currentQuantity == null ? '-' : `${request.currentQuantity}${request.unit || '개'}`

  async function handleAction(nextStatus) {
    setSaving(true)
    setError('')
    try {
      await onStatusChange(request.id, { status: nextStatus, managerMemo: managerMemo.trim() })
    } catch (err) {
      setError(err.message || '상태를 변경하지 못했습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal-panel modal-panel--form"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-detail-title"
      >
        <h2 id="request-detail-title" className="modal-title">
          요청 상세
        </h2>
        <div className="request-selected">
          <p>
            <span>상태</span>
            <StatusBadge kind="request" status={request.status} />
          </p>
          <p>
            <span>구분</span>
            <strong>{isNew ? '미등록품목' : '기존품목'}</strong>
          </p>
          <p>
            <span>품목명</span>
            <strong>{requestItemName(request)}</strong>
          </p>
          <p>
            <span>제조사</span>
            <strong>{request.manufacturer || '-'}</strong>
          </p>
          <p>
            <span>모델명</span>
            <strong className="model-name">{request.model || '-'}</strong>
          </p>
          <p>
            <span>사양</span>
            <strong>{request.specification || '-'}</strong>
          </p>
          {!isNew ? (
            <p>
              <span>요청 당시 재고</span>
              <strong>{currentStock}</strong>
            </p>
          ) : null}
          <p>
            <span>요청수량</span>
            <strong>
              {request.requestedQty}
              {request.unit || '개'}
            </strong>
          </p>
          <p>
            <span>구매처 링크</span>
            <strong><PurchaseLink url={request.purchaseUrl} /></strong>
          </p>
          <p>
            <span>예상 단가</span>
            <strong>{formatUnitPrice(request.unitPrice)}</strong>
          </p>
          <p>
            <span>예상 합계</span>
            <strong>{formatExpectedTotal(request.requestedQty, request.unitPrice)}</strong>
          </p>
          <p>
            <span>견적서</span>
            <strong>
              {request.quotation?.fileName ? (
                <button type="button" className="btn btn--small" onClick={() => onOpenQuotation(request.id)}>
                  견적서 열기
                </button>
              ) : '-'}
            </strong>
          </p>
          <p>
            <span>요청자</span>
            <strong>{request.requesterName || '-'}</strong>
          </p>
          <p>
            <span>부서</span>
            <strong>{request.requesterDepartment || '-'}</strong>
          </p>
          <p>
            <span>요청일</span>
            <strong>{formatDateTime(request.createdAt)}</strong>
          </p>
          <p>
            <span>요청사유</span>
            <strong>{request.reason || '-'}</strong>
          </p>
          {request.source === 'FILE_IMPORT' ? (
            <p>
              <span>출처</span>
              <strong>견적서 일괄 요청{request.supplier ? ` · ${request.supplier}` : ''}</strong>
            </p>
          ) : null}
        </div>

        {canManage ? (
          <>
            <label className="field">
              <span>관리자 메모</span>
              <textarea
                className="input input--area"
                value={managerMemo}
                onChange={(e) => setManagerMemo(e.target.value)}
                placeholder="승인·반려 등 처리 시 남길 메모"
              />
            </label>
            <div className="field">
              <span>상태 변경</span>
              <RequestStatusActions status={request.status} disabled={saving} onSelect={handleAction} size="touch" />
            </div>
          </>
        ) : request.managerMemo ? (
          <p className="muted">관리자 메모: {request.managerMemo}</p>
        ) : null}

        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal-actions">
          {canEdit ? (
            <button type="button" className="btn btn--primary btn--touch" onClick={() => onEdit(request)} disabled={saving}>
              수정
            </button>
          ) : null}
          <button type="button" className="btn btn--ghost btn--touch" onClick={onClose} disabled={saving}>
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}

export default function RequestsPage() {
  const { user } = useAuth()
  const { show } = useToast()
  const manageRequests = canManageRequests(user)
  const [params, setParams] = useSearchParams()
  const [status, setStatus] = useState(params.get('status') || 'all')
  const [requestType, setRequestType] = useState(params.get('type') || 'all')
  const [page, setPage] = useState(parsePage(params.get('page')))
  const [limit, setLimit] = useState(parseLimit(params.get('limit')))
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [quoteOpen, setQuoteOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const [editing, setEditing] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  function syncParams(next) {
    const merged = { status, type: requestType, page, limit, ...next }
    const search = new URLSearchParams()
    if (merged.status && merged.status !== 'all') search.set('status', merged.status)
    if (merged.type && merged.type !== 'all') search.set('type', merged.type)
    if (Number(merged.page) > 1) search.set('page', String(merged.page))
    if (Number(merged.limit) && Number(merged.limit) !== 20) search.set('limit', String(merged.limit))
    setParams(search, { replace: true })
  }

  function changeFilters(next) {
    if (next.status !== undefined) setStatus(next.status)
    if (next.type !== undefined) setRequestType(next.type)
    setPage(1)
    syncParams({ ...next, page: 1 })
  }

  function changePage(nextPage) {
    setPage(nextPage)
    syncParams({ page: nextPage })
  }

  function changeLimit(nextLimit) {
    setLimit(nextLimit)
    setPage(1)
    syncParams({ limit: nextLimit, page: 1 })
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      setListLoading(true)
      setListError('')
      try {
        const result = await partRequestApi.fetchPartRequestsPage({
          status,
          requestType,
          page,
          limit,
        })
        if (cancelled) return
        setItems(result.items)
        setTotal(result.total)
        setTotalPages(result.totalPages)
        if (result.page !== page) {
          setPage(result.page)
          syncParams({ page: result.page })
        }
      } catch (err) {
        if (!cancelled) setListError(err.message || '요청 목록을 불러오지 못했습니다.')
      } finally {
        if (!cancelled) setListLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [status, requestType, page, limit, reloadKey])

  async function openQuotation(id) {
    try {
      const quotation = await partRequestApi.fetchPartRequestQuotation(id)
      if (quotation?.url) window.open(quotation.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      show(err.message || '견적서를 열지 못했습니다.', 'error')
    }
  }

  async function handleCreate(payload, file) {
    await partRequestApi.createPartRequest(payload, file)
    show('필요 품목 요청을 등록했습니다.')
    setCreateOpen(false)
    setPage(1)
    syncParams({ page: 1 })
    setReloadKey((value) => value + 1)
  }

  function openEdit(request) {
    setDetail(null)
    setEditing(request)
  }

  async function handleUpdate(id, payload, file) {
    const updated = await partRequestApi.updatePartRequest(id, payload, file)
    show('필요 품목 요청을 수정했습니다.')
    setEditing(null)
    setItems((current) => current.map((item) => (item.id === updated.id ? updated : item)))
    setDetail(updated)
  }

  async function handleStatusChange(id, payload) {
    const updated = await partRequestApi.updatePartRequestStatus(id, payload)
    show('요청 상태를 변경했습니다.')
    setItems((current) => current.map((item) => (item.id === updated.id ? updated : item)))
    setDetail(updated)
    return updated
  }

  async function handleQuickStatus(id, nextStatus) {
    try {
      const updated = await partRequestApi.updatePartRequestStatus(id, { status: nextStatus })
      show('요청 상태를 변경했습니다.')
      setItems((current) => current.map((item) => (item.id === updated.id ? updated : item)))
      if (detail?.id === updated.id) setDetail(updated)
    } catch (err) {
      show(err.message || '상태를 변경하지 못했습니다.', 'error')
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="필요 품목 요청"
        description={
          manageRequests
            ? '직원 요청을 확인하고 상태를 관리합니다. 재고수량과 입출고 이력은 변경되지 않습니다.'
            : '필요한 품목을 요청합니다. 재고에 없는 신규 품목도 요청할 수 있습니다.'
        }
        actions={
          <>
            <button type="button" className="btn btn--ghost btn--touch" onClick={() => setQuoteOpen(true)}>
              견적서로 일괄 요청
            </button>
            <button type="button" className="btn btn--primary btn--touch" onClick={() => setCreateOpen(true)}>
              + 필요 품목 요청
            </button>
          </>
        }
      />

      {listError ? <p className="form-error">{listError}</p> : null}

      <section className="filter-panel">
        <div className="filter-toolbar filter-toolbar--stack">
          <div className="filter-group">
            <span className="filter-group__label">요청 상태</span>
            <div className="segmented" role="group" aria-label="요청 상태">
              {PART_REQUEST_STATUS_FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`segmented__btn ${status === item.value ? 'is-active' : ''}`}
                  onClick={() => changeFilters({ status: item.value })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <span className="filter-group__label">품목 구분</span>
            <div className="segmented" role="group" aria-label="품목 구분">
              {PART_REQUEST_TYPE_FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`segmented__btn ${requestType === item.value ? 'is-active' : ''}`}
                  onClick={() => changeFilters({ type: item.value })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <p className="result-count">
        {listLoading ? '불러오는 중...' : `총 ${total.toLocaleString('ko-KR')}건`}
      </p>

      {items.length === 0 ? (
        <p className="empty">{listLoading ? '요청 목록을 불러오는 중입니다.' : '조건에 맞는 요청이 없습니다.'}</p>
      ) : (
        <>
          <div className="card-list">
            {items.map((item) => (
              <article key={item.id} className="part-card">
                <button type="button" className="part-card__main" onClick={() => setDetail(item)}>
                  <div className="part-card__top">
                    <span className="muted">{item.requestType === 'NEW_ITEM' ? '미등록품목' : '기존품목'}</span>
                    <StatusBadge kind="request" status={item.status} />
                  </div>
                  <strong className="model-name">{requestItemName(item)}</strong>
                  <span>
                    {item.manufacturer || '-'} · {item.model || '-'}
                  </span>
                  <span className="muted">
                    {item.requesterName} · {item.requesterDepartment || '-'} · {formatDateTime(item.createdAt)}
                  </span>
                  <div className="part-card__qty">
                    <b>
                      {item.requestedQty}
                      {item.unit || '개'}
                    </b>
                    <span>{formatUnitPrice(item.unitPrice)}</span>
                  </div>
                </button>
                <div className="request-card-actions">
                  <PurchaseLink url={item.purchaseUrl} />
                  {item.quotation?.fileName ? (
                    <button type="button" className="btn btn--small" onClick={() => openQuotation(item.id)}>견적서</button>
                  ) : null}
                  {canEditPartRequest(user, item) ? (
                    <button type="button" className="btn btn--small" onClick={() => openEdit(item)}>수정</button>
                  ) : null}
                </div>
                {manageRequests ? (
                  <RequestStatusActions status={item.status} onSelect={(nextStatus) => handleQuickStatus(item.id, nextStatus)} />
                ) : null}
              </article>
            ))}
          </div>

          <div className="table-wrap">
            <table className="data-table requests-table">
              <thead>
                <tr>
                  <th>상태</th>
                  <th>구분</th>
                  <th>품목명</th>
                  <th>제조사</th>
                  <th>모델명</th>
                  <th>요청수량</th>
                  <th>구매처</th>
                  <th>예상단가</th>
                  <th>견적서</th>
                  <th>요청자</th>
                  <th>부서</th>
                  <th>요청일</th>
                  <th>수정</th>
                  {manageRequests ? <th>상태변경</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <StatusBadge kind="request" status={item.status} />
                    </td>
                    <td>{item.requestType === 'NEW_ITEM' ? '미등록품목' : '기존품목'}</td>
                    <td className="text-cell">
                      <button type="button" className="model-link model-name" onClick={() => setDetail(item)}>
                        {requestItemName(item)}
                      </button>
                    </td>
                    <td className="text-cell">{item.manufacturer || '-'}</td>
                    <td className="text-cell model-name">{item.model || '-'}</td>
                    <td className="td-qty">
                      {item.requestedQty}
                      {item.unit || '개'}
                    </td>
                    <td><PurchaseLink url={item.purchaseUrl} /></td>
                    <td className="td-qty">{formatUnitPrice(item.unitPrice)}</td>
                    <td>
                      {item.quotation?.fileName ? (
                        <button type="button" className="btn btn--small" onClick={() => openQuotation(item.id)}>견적서</button>
                      ) : '-'}
                    </td>
                    <td>{item.requesterName || '-'}</td>
                    <td className="text-cell">{item.requesterDepartment || '-'}</td>
                    <td>{formatCompactDateTime(item.createdAt)}</td>
                    <td>
                      {canEditPartRequest(user, item) ? (
                        <button type="button" className="btn btn--small" onClick={() => openEdit(item)}>수정</button>
                      ) : '-'}
                    </td>
                    {manageRequests ? (
                      <td>
                        <RequestStatusActions
                          layout="table"
                          status={item.status}
                          onSelect={(nextStatus) => handleQuickStatus(item.id, nextStatus)}
                        />
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        disabled={listLoading}
        itemLabel="개 요청"
        limitLabel="페이지당 요청 수"
        onPageChange={changePage}
        onLimitChange={changeLimit}
      />

      <PartRequestModal open={createOpen} onClose={() => setCreateOpen(false)} onSubmit={handleCreate} />
      <QuoteImportModal
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        onSubmitted={() => {
          show('일괄 필요품목 요청을 등록했습니다.')
          setQuoteOpen(false)
          setPage(1)
          syncParams({ page: 1 })
          setReloadKey((value) => value + 1)
        }}
      />
      <RequestDetailModal
        open={Boolean(detail)}
        request={detail}
        canManage={manageRequests}
        canEdit={canEditPartRequest(user, detail)}
        onClose={() => setDetail(null)}
        onEdit={openEdit}
        onOpenQuotation={openQuotation}
        onStatusChange={handleStatusChange}
      />
      <PartRequestEditModal
        open={Boolean(editing)}
        request={editing}
        onClose={() => setEditing(null)}
        onSubmit={handleUpdate}
      />
    </div>
  )
}
